<template>
  <div class="influencer-create">
    <div class="page-header">
      <div class="page-header-inner">
        <a class="back-link" :href="'#/influencers'">
          <i class="bi bi-arrow-left" aria-hidden="true"></i>
          Back to influencers
        </a>
        <div class="title-row">
          <div>
            <h1 class="page-title">{{ isEdit ? 'Edit influencer' : 'Add influencer' }}</h1>
            <p class="page-subtitle">Required: name, category, type and at least one profile link.</p>
          </div>
          <button class="btn-ai" @click="aiOpen = true" title="Find public details about this influencer with AI">
            <i class="bi bi-stars" aria-hidden="true"></i>
            Look up with AI
          </button>
        </div>
      </div>
    </div>

    <ai-suggest-modal
      :open="aiOpen"
      :initial-name="influencer.name || ''"
      :initial-firstname="influencer.firstname || ''"
      :initial-lastname="influencer.lastname || ''"
      :initial-channels="aiInitialChannels"
      @close="aiOpen = false"
      @apply="applyAiSuggestion"
    />

    <div class="page-body">

      <section class="form-section">
        <div class="section-head">
          <div class="section-icon">
            <i class="bi bi-person-fill" style="font-size:18px" aria-hidden="true"></i>
          </div>
          <div>
            <h2 class="section-title">Basics</h2>
            <p class="section-desc">Names used to list and find the influencer, plus category and type.</p>
          </div>
        </div>

        <div class="section-body">
          <div class="form-group">
            <label class="form-label" for="inf-name">Name<span class="required">*</span></label>
            <input id="inf-name" v-model="influencer.name" type="text" class="form-control" autofocus>
          </div>
          <div class="row g-3">
            <div class="col-sm-6 form-group">
              <label class="form-label" for="inf-firstname">First name</label>
              <input id="inf-firstname" v-model="influencer.firstname" type="text" class="form-control">
            </div>
            <div class="col-sm-6 form-group">
              <label class="form-label" for="inf-lastname">Last name</label>
              <input id="inf-lastname" v-model="influencer.lastname" type="text" class="form-control">
            </div>
          </div>

          <div class="form-group">
            <label class="form-label" for="inf-nickname">
              Also known as
              <span class="help">Other names or handles, so the influencer is easier to find.</span>
            </label>
            <input id="inf-nickname" v-model="influencer.nickname" type="text" class="form-control">
          </div>

          <div class="row g-3">
            <div class="col-sm-6 form-group">
              <label class="form-label" for="inf-category">Category<span class="required">*</span></label>
              <select id="inf-category" v-model="influencer.category_id" class="form-control">
                <option :value="undefined" disabled>Choose…</option>
                <option v-for="(c, id) in category" v-bind:value="id" :key="'c'+id">{{c}}</option>
              </select>
            </div>
            <div class="col-sm-6 form-group">
              <label class="form-label" for="inf-type">Type<span class="required">*</span></label>
              <select id="inf-type" v-model="influencer.type_id" class="form-control">
                <option :value="undefined" disabled>Choose…</option>
                <option v-for="(t, id) in type" v-bind:value="id" :key="'t'+id">{{t}}</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <section class="form-section profiles-section">
        <div class="section-head">
          <div class="section-icon">
            <i class="bi bi-link-45deg" style="font-size:20px" aria-hidden="true"></i>
          </div>
          <div class="flex-grow-1">
            <h2 class="section-title">
              Profiles<span class="required">*</span>
              <span class="badge-count" v-if="authors.length">{{ authors.length }}</span>
            </h2>
            <p class="section-desc">Paste a link to the influencer's social profile and press Enter. Add as many as you have.</p>
          </div>
        </div>

        <div class="section-body">
          <new-author @addAuthor="addAuthor($event)"></new-author>

          <div class="authors-list" v-if="authors.length">
            <author
              v-for="(author, idx) in authors"
              :key="idx"
              :idx="idx"
              :data="author"
              @removeAuthor="removeAuthor($event)"
            />
          </div>
        </div>
      </section>

      <suggested-channels
        :channels="suggestedChannels"
        :pending-urls="pendingChannelUrls"
        @accept="acceptSuggestion"
        @dismiss="dismissSuggestion"
        @accept-all="acceptAllSuggestions"
        @dismiss-all="dismissAllSuggestions"
      />

      <section class="form-section">
        <div class="section-head">
          <div class="section-icon">
            <i class="bi bi-person-circle" style="font-size:18px" aria-hidden="true"></i>
          </div>
          <div>
            <h2 class="section-title">Person</h2>
            <p class="section-desc">Details about the person.</p>
          </div>
        </div>

        <div class="section-body">
          <div class="row g-3">
            <div class="col-sm-6 form-group">
              <label class="form-label">Gender</label>
              <div class="seg" role="group" aria-label="Gender">
                <button type="button" class="seg-btn" :class="{ 'is-active': isGender('2') }" @click="setGender('2')">Female</button>
                <button type="button" class="seg-btn" :class="{ 'is-active': isGender('1') }" @click="setGender('1')">Male</button>
                <button type="button" class="seg-btn" :class="{ 'is-active': isGender('0') }" @click="setGender('0')">Unknown</button>
              </div>
            </div>
            <div class="col-sm-6 form-group">
              <label class="form-label" for="inf-age">Birth year</label>
              <input id="inf-age" v-model="influencer.age" type="text" inputmode="numeric" maxlength="4" class="form-control">
            </div>
          </div>

          <div class="row g-3">
            <div class="col-sm-6 form-group">
              <label class="form-label" for="inf-location">Location</label>
              <input id="inf-location" v-model="influencer.location" type="text" class="form-control">
            </div>
            <div class="col-sm-6 form-group">
              <label class="form-label" for="inf-internship">Years of activity</label>
              <input id="inf-internship" v-model="influencer.internship" type="text" inputmode="numeric" class="form-control">
            </div>
          </div>
          <div class="row g-3">
            <div class="col-sm-12 form-group">
              <label class="form-label" for="inf-lang">Language</label>
              <select id="inf-lang" v-model="influencer.lang" class="form-control">
                <option value="pl">Polish</option>
                <option value="cs">Czech</option>
                <option value="bg">Bulgarian</option>
                <option value="un">Unknown</option>
              </select>
            </div>

          </div>
        </div>
      </section>

      <section class="form-section">
        <div class="section-head">
          <div class="section-icon">
            <i class="bi bi-envelope-fill" style="font-size:18px" aria-hidden="true"></i>
          </div>
          <div>
            <h2 class="section-title">Contact</h2>
            <p class="section-desc">For internal use only. Not visible to the influencer.</p>
          </div>
        </div>

        <div class="section-body">
          <div class="row g-3">
            <div class="col-sm-6 form-group">
              <label class="form-label" for="inf-email">E-mail</label>
              <input id="inf-email" v-model="influencer.email" type="email" class="form-control">
            </div>
            <div class="col-sm-6 form-group">
              <label class="form-label" for="inf-phone">Phone</label>
              <input id="inf-phone" v-model="influencer.phone" type="tel" class="form-control">
            </div>
          </div>
          <div class="form-group">
            <label class="form-label" for="inf-site">Website</label>
            <input id="inf-site" v-model="influencer.site" type="url" class="form-control">
          </div>
        </div>
      </section>


      <section class="danger-zone" v-if="isEdit">
        <div class="danger-zone-head">
          <h3>Delete influencer</h3>
          <p>This cannot be undone. Linked profiles and audience data will be detached.</p>
        </div>
        <button class="btn btn-outline-danger" @click="removeInfluencer()">
          <i class="bi bi-trash3" aria-hidden="true"></i>
          Delete influencer
        </button>
      </section>
    </div>

    <div class="action-bar">
      <div class="action-bar-inner">
        <div class="action-bar-status">
          <span class="dot" :class="isValid ? 'dot-ok' : 'dot-warn'"></span>
          <span v-if="isValid">Ready to save</span>
          <span v-else>Missing: {{ missingList }}</span>
        </div>
        <div class="action-bar-buttons">
          <a class="btn btn-outline-secondary" :href="'#/influencers'">Cancel</a>
          <button v-on:click="saveInfluencer()" class="btn btn-primary px-4" :disabled="!isValid">
            {{ isEdit ? 'Save changes' : 'Create influencer' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
  import Author from "./author/Author";
  import NewAuthor from "./author/NewAuthor";
  import AiSuggestModal from "./AiSuggestModal.vue";
  import SuggestedChannels from "./SuggestedChannels.vue";
  import { detectPlatformHint, validateChannel, stripQuery } from "../../utils/socialChannels";

  export default {
    components: {
      NewAuthor,
      Author,
      AiSuggestModal,
      SuggestedChannels
    },
    name: "influencer-create",
    data() {
      return {
        influencer: {
          id: 0,
          gender: '0',
          lang: 'pl'
        },
        authors: [],
        category: {},
        type: {},
        aiOpen: false,
        suggestedChannels: [],
        pendingChannelUrls: [],
        apiHost: this.$appSettings.getSetting('host')
      }
    },
    computed: {
      isEdit() {
        return !!this.influencer.id;
      },
      aiInitialChannels() {
        return (this.authors || [])
          .filter(a => a && a.url)
          .map(a => ({
            url: a.url,
            platform: detectPlatformHint(a.url) || ''
          }));
      },
      isValid() {
        return this.missing.length === 0;
      },
      missing() {
        const m = [];
        if (!this.influencer.name) m.push('name');
        if (this.influencer.category_id === undefined) m.push('category');
        if (this.influencer.type_id === undefined) m.push('type');
        if (this.authors.length === 0) m.push('a profile link');
        return m;
      },
      missingList() {
        return this.missing.join(', ');
      }
    },
    methods: {
      isGender(v) {
        return String(this.influencer.gender) === v;
      },
      setGender(v) {
        this.$set(this.influencer, 'gender', v);
      },
      applyAiSuggestion(data, fields) {
        if (!data) return;
        const list = (fields && fields.length) ? fields : [];
        const set = (key, value) => {
          this.$set(this.influencer, key, value);
        };
        if (list.indexOf('firstname') !== -1) set('firstname', data.firstname);
        if (list.indexOf('lastname') !== -1) set('lastname', data.lastname);
        if (list.indexOf('nickname') !== -1) set('nickname', data.nickname);
        if (list.indexOf('location') !== -1) set('location', data.location);
        if (list.indexOf('email') !== -1) set('email', data.email);
        if (list.indexOf('site') !== -1) set('site', data.site);
        if (list.indexOf('gender') !== -1) set('gender', String(data.gender));
        if (list.indexOf('age') !== -1) {
          const birthYear = new Date().getFullYear() - data.age;
          set('age', String(birthYear));
        }
        if (list.indexOf('lang') !== -1) set('lang', String(data.lang).toLowerCase());

        let suggestedCount = 0;
        if (list.indexOf('channels') !== -1 && Array.isArray(data.channels)) {
          const known = new Set([
            ...this.authors.map(a => (a.url || '').toLowerCase()),
            ...this.suggestedChannels.map(s => (s.url || '').toLowerCase())
          ]);
          for (const ch of data.channels) {
            const url = (ch.url || '').trim();
            if (!url) continue;
            if (known.has(url.toLowerCase())) continue;
            known.add(url.toLowerCase());
            this.suggestedChannels.push({
              url: url,
              platform: ch.platform || detectPlatformHint(url) || '',
              username: ch.username || ''
            });
            suggestedCount++;
          }
        }

        const appliedCount = list.indexOf('channels') !== -1 ? list.length - 1 : list.length;
        let notifyText = 'Applied ' + appliedCount + ' field' + (appliedCount === 1 ? '' : 's');
        if (suggestedCount > 0) {
          notifyText += ' · ' + suggestedCount + ' channel' + (suggestedCount === 1 ? '' : 's') + ' added to suggestions';
        }
        this.$notify({
          group: 'default',
          type: 'success',
          text: notifyText
        });
        this.aiOpen = false;
      },
      acceptSuggestion(idx) {
        const ch = this.suggestedChannels[idx];
        if (!ch) return;
        this.checkAndAcceptSuggestion(ch);
      },
      acceptAllSuggestions() {
        const copy = this.suggestedChannels.slice();
        for (const ch of copy) {
          this.checkAndAcceptSuggestion(ch);
        }
      },
      checkAndAcceptSuggestion(ch) {
        if (!ch || !ch.url) return;
        if (this.pendingChannelUrls.indexOf(ch.url) !== -1) return;

        const cleaned = stripQuery(ch.url);
        if (!cleaned) {
          this.removeSuggestionByUrl(ch.url);
          return;
        }

        const result = validateChannel(cleaned);
        if (!result.valid) {
          this.$notify({
            group: 'default',
            type: 'error',
            text: cleaned + ' — ' + (result.error || 'Invalid URL format')
          });
          return;
        }

        const finalUrl = result.cleanUrl;
        this.pendingChannelUrls.push(ch.url);

        this.$http.post(this.apiHost + 'influencers/check/author',
          { url: finalUrl },
          { params: { token: this.$auth.getToken() } }
        ).then(response => {
          this.removePendingUrl(ch.url);
          const body = (response && response.body) || {};
          const checkData = (body.data) || {};
          if (checkData.check) {
            this.addAuthor({ url: finalUrl, picture: '' });
            this.removeSuggestionByUrl(ch.url);
          } else {
            this.$notify({
              group: 'default',
              type: 'error',
              text: 'This profile is already assigned to influencer #' + checkData.influencerId
            });
          }
        }, () => {
          this.removePendingUrl(ch.url);
          this.$notify({
            group: 'default',
            type: 'error',
            text: 'Failed to verify channel: ' + finalUrl
          });
        });
      },
      removePendingUrl(url) {
        const i = this.pendingChannelUrls.indexOf(url);
        if (i !== -1) this.pendingChannelUrls.splice(i, 1);
      },
      removeSuggestionByUrl(url) {
        const i = this.suggestedChannels.findIndex(s => s.url === url);
        if (i !== -1) this.suggestedChannels.splice(i, 1);
      },
      dismissSuggestion(idx) {
        this.suggestedChannels.splice(idx, 1);
      },
      dismissAllSuggestions() {
        this.suggestedChannels = [];
      },
      getInfluencerData(id) {
        this.$http.get(this.apiHost + 'influencers/get', {
          params: {
            token: this.$auth.getToken(),
            id: id
          }
        }).then(response => {
          if (response.body.success) {
            this.influencer = response.body.data.influencer;
            this.authors = response.body.data.authors;
            this.category = response.body.data.category;
          }
        })
      },
      getInfluencerCategory() {
        this.$http.get(this.apiHost + 'influencers/category/get', {
          params: {
            token: this.$auth.getToken()
          }
        }).then(response => {
          if (response.body.success) {
            this.category = response.body.data.list;
          }
        });
      },
      getInfluencerType() {
        this.$http.get(this.apiHost + 'influencers/type/get', {
          params: {
            token: this.$auth.getToken()
          }
        }).then(response => {
          if (response.body.success) {
            this.type = response.body.data.list;
          }
        });
      },
      getData() {
        if (this.$route.params.id !== undefined) {
          console.log(this.$route.params.id);
          this.getInfluencerData(this.$route.params.id);
        } else {
          this.getInfluencerCategory();
        }
        this.getInfluencerType();
      },
      saveInfluencer() {
        if (this.influencer.id) {
          //update
          if (!this.isValid) {
            this.$notify({
              group: 'default',
              type: 'error',
              text: 'Add a name, category, type and at least one profile link'
            })
          } else {
            this.$http.post(this.apiHost + 'influencers/update', this.influencer, {
              //emulateJSON: true,
              headers: {
                Authorization: 'Bearer ' + this.$auth.getToken()
              },
              params: {
                token: this.$auth.getToken()
              }
            }).then(response => {
              if (response.body.success) {
                this.$notify({
                  group: 'default',
                  type: 'success',
                  text: 'Influencer has been updated'
                })
              }
            })
          }

        } else {
          //save new
          if (!this.isValid) {
            this.$notify({
              group: 'default',
              type: 'error',
              text: 'Add a name, category, type and at least one profile link'
            })
          } else {
            this.$http.post(this.apiHost + 'influencers/new', {
              'influencer': this.influencer,
              'authors': this.authors
            }, {
              params: {
                token: this.$auth.getToken()
              }
            }).then(response => {
              if (response.body.success) {
                var url = '#/influencer/show/' + response.body.data.id;
                var win = window.open(url, '_self');
                win.focus();
              }
            })
          }
        }
      },
      removeInfluencer() {
        if (this.influencer.id > 0) {
          this.$http.delete(this.apiHost + 'influencers/remove/influencer', {
            params: {
              token: this.$auth.getToken(),
              id: this.influencer.id
            }
          }).then(response => {
            if (response.body.success) {
              var url = '#/influencers';
              var win = window.open(url, '_self');
              win.focus();

              this.$notify({
                group: 'default',
                type: 'success',
                text: 'Influencer has been deleted'
              });
            }
          })
        }
      },

      removeAuthor($event) {
        if (this.influencer.id > 0) {
          //delete from server
          this.$http.delete(this.apiHost + 'influencers/remove/author', {
            params: {
              token: this.$auth.getToken(),
              influencer_id: this.influencer.id,
              author_id: this.authors[$event.idx].author_id
            }
          }).then(response => {
            if (response.body.success) {
              this.authors.splice($event.idx, 1);
            }
          })
        } else {
          this.authors.splice($event.idx, 1);
        }
        console.log($event);
      },
      addAuthor($event) {
        //this.authors.push($event);
        if (this.influencer.id > 0) {
          //add on server
          console.log('add author to server');
          this.$http.post(this.apiHost + 'influencers/add/author',{
            influencer_id: this.influencer.id,
            url: $event.url,
            picture: $event.picture
          }, {
            headers: {
              Authorization: 'Bearer ' + this.$auth.getToken()
            },
            params: {
              token: this.$auth.getToken()
            }
          }).then(response => {
            if (response.body.success) {
              this.$notify({
                group: 'default',
                type: 'success',
                text: 'Profile added'
              });
              this.authors.push(response.body.data);
            }
          })
        } else {
          //add to array
          this.authors.push({
            id: 0,
            url: $event.url,
            picture: $event.picture
          });

        }
      }
    },
    created: function() {
      this.getData();
    }
  }
</script>

<style scoped>
  .influencer-create {
    max-width: 880px;
    margin: 0 auto 160px;
    color: #1f2937;
  }

  .page-header {
    margin-bottom: 28px;
  }
  .page-header-inner {
    padding: 8px 4px 0;
  }
  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
  }
  .btn-ai {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #265a88 0%, #833ab4 100%);
    color: #fff;
    border: 0;
    border-radius: 9px;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(38, 90, 136, 0.22);
    transition: transform 0.1s, box-shadow 0.15s;
    flex-shrink: 0;
  }
  .btn-ai:hover {
    box-shadow: 0 6px 18px rgba(38, 90, 136, 0.32);
    transform: translateY(-1px);
  }
  @media (max-width: 540px) {
    .title-row {
      flex-direction: column;
      align-items: stretch;
    }
    .btn-ai { justify-content: center; }
  }
  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 14px;
    text-decoration: none;
    transition: color 0.15s;
  }
  .back-link:hover {
    color: #265a88;
  }
  .page-title {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0 0 6px;
    color: #0f172a;
  }
  .page-subtitle {
    color: #6b7280;
    font-size: 14px;
    margin: 0;
  }

  .page-body {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .form-section {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .section-head {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
    background: #fafbfc;
  }
  .section-icon {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #eaf1f9;
    color: #265a88;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .section-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 2px;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .section-desc {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }
  .section-body {
    padding: 22px 24px;
  }

  .badge-count {
    background: #265a88;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    letter-spacing: 0.02em;
  }

  .form-group {
    margin-bottom: 16px;
  }
  .form-group:last-child {
    margin-bottom: 0;
  }
  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }
  .form-control {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    transition: border-color 0.15s, box-shadow 0.15s;
    background: #fff;
  }
  .form-control:focus {
    border-color: #265a88;
    box-shadow: 0 0 0 3px rgba(38, 90, 136, 0.12);
    outline: none;
  }

  .required {
    color: #dc2626;
    margin-left: 2px;
  }
  .help {
    display: block;
    font-weight: 400;
    color: #6b7280;
    font-size: 12px;
    margin-top: 2px;
  }

  .seg {
    display: flex;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
  }
  .seg-btn {
    flex: 1;
    padding: 10px 12px;
    font-size: 14px;
    font-weight: 500;
    color: #475569;
    background: transparent;
    border: 0;
    border-right: 1px solid #d1d5db;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .seg-btn:last-child { border-right: 0; }
  .seg-btn:hover { background: #f8fafc; color: #0f172a; }
  .seg-btn.is-active {
    background: #eaf1f9;
    color: #265a88;
    font-weight: 600;
  }
  .seg-btn:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(38, 90, 136, 0.35);
  }

  .authors-list {
    margin-top: 18px;
    border-top: 1px dashed #e5e7eb;
    padding-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .danger-zone {
    margin-top: 8px;
    padding: 20px 24px;
    border: 1px solid #fecaca;
    border-radius: 14px;
    background: #fef2f2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  .danger-zone-head h3 {
    font-size: 14px;
    font-weight: 600;
    color: #991b1b;
    margin: 0 0 2px;
  }
  .danger-zone-head p {
    font-size: 12.5px;
    color: #b91c1c;
    margin: 0;
    max-width: 520px;
  }
  .danger-zone .btn-outline-danger {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
  }

  .action-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: saturate(180%) blur(12px);
    -webkit-backdrop-filter: saturate(180%) blur(12px);
    border-top: 1px solid #e5e7eb;
    box-shadow: 0 -4px 20px rgba(15, 23, 42, 0.06);
    z-index: 50;
  }
  .action-bar-inner {
    max-width: 880px;
    margin: 0 auto;
    padding: 14px 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
  .action-bar-status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #6b7280;
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }
  .dot-ok {
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
  }
  .dot-warn {
    background: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.18);
  }
  .action-bar-buttons {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .action-bar-buttons .btn {
    display: inline-flex;
    align-items: center;
    font-weight: 500;
  }
  .btn-primary:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    .section-head {
      padding: 16px;
    }
    .section-body {
      padding: 16px;
    }
    .danger-zone {
      flex-direction: column;
      align-items: flex-start;
    }
    .action-bar-inner {
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
      padding: 12px 16px;
    }
    .action-bar-buttons {
      justify-content: stretch;
    }
    .action-bar-buttons .btn {
      flex: 1;
      justify-content: center;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/influencer/InfluencerCreate.vue